import React, {useState} from 'react';
import { Button, TextField, IconButton, InputAdornment } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import PasswordChecklist from 'react-password-checklist';
import {toast} from 'react-toastify';
import {Auth} from 'aws-amplify';

const useStyles = makeStyles((theme) => ({
    updatePasswordContainer: {
        width: '100%',
        padding: theme.spacing(2),
    },
    cardTitle: {
        marginBottom: theme.spacing(1),
        fontSize: '18px',
    },
    inputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        marginRight: '-15px',
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            width: '100%',
            backgroundColor: '#ffffff',
            '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#047d95',
            },
        },
        '& .MuiInputBase-input': {
            height: '3em',
            boxSizing: 'border-box',
            color: '#000000',
            '&::placeholder': {
                color: 'rgba(0, 0, 0, 0.4)',
                opacity: 1,
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.6)',
            '&.Mui-focused': {
                color: '#047d95',
            },
        },
    },
    resendCodeButton: {
        color: '#047d95',
        backgroundColor: 'transparent',
        padding: theme.spacing(0.5, 1.5),
        width: '100%',
        borderRadius: '4px',
        transition: 'background-color 0.2s ease-in-out',
        border: 'none',
        textTransform: 'none',
        fontSize: '14px',
        '&:hover': {
            backgroundColor: 'rgba(4, 125, 149, 0.1)',
        },
    },
    sendChangePasswordRequest: {
        backgroundColor: '#FF9900',
        textTransform: 'uppercase',
        color: 'white',
        padding: theme.spacing(1, 0),
        minWidth: '120px',
        fontWeight: 400,
        fontFamily: 'Arial',
        letterSpacing: '1.1px',
        fontSize: '12px',
        lineHeight: '18px',
        borderRadius: 0,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#FF9900',
            color: 'white',
        },
    },
    iconButton: {
        padding: theme.spacing(0.5),
        color: '#000000',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    },
    inputAdornment: {
        height: '100%',
    },
    divider: {
        height: '45px',
        borderLeft: '1px solid #ccc',
        marginRight: theme.spacing(0.5),
    },
}));

export default function CognitoVerifyChangePassword(props) {
    const CLASSES = useStyles();
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const resendCode = () => {
        Auth.forgotPassword(props.email);
    };

    const requestPasswordChange = () => {
        if (!password) {
            toast("Password cannot be empty.", {position: "top-center", type: "error"});
            return;
        }
        if (password !== confirmPassword) {
            toast("Passwords must match.", {position: "top-center", type: "error"});
            return;
        }

        Auth.forgotPasswordSubmit(props.email, code, password)
            .then(() => {
                toast("Password updated successfully", {position: "top-center", type: "success"});
                props.setShowResetPassword(false);
            })
            .catch((e) => {
                toast("Error updating password", {position: "top-center", type: "error"});
                console.error('Error:', e);
                props.setShowResetPassword(false);
            });
    };

    const shouldDisplayChecklist = password && confirmPassword && password !== confirmPassword;

    return (
        <div className={CLASSES.updatePasswordContainer}>
            <div className={`gatekeeperTitle ${CLASSES.cardTitle}`}>Reset your password</div>
            <fieldset className={CLASSES.inputsContainer}>
                <TextField
                    className={CLASSES.textField}
                    id="codeInput"
                    label="Code"
                    placeholder="Code"
                    variant="outlined"
                    inputProps={{
                        autoComplete: 'one-time-code'
                    }}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    fullWidth
                />

                <TextField
                    className={CLASSES.textField}
                    id="passwordInput"
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New Password"
                    variant="outlined"
                    inputProps={{
                        autoComplete: 'new-password'
                    }}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" className={CLASSES.inputAdornment}>
                                <div className={CLASSES.divider}></div>
                                <IconButton
                                    className={CLASSES.iconButton}
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <TextField
                    className={CLASSES.textField}
                    id="confirmPasswordInput"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    variant="outlined"
                    inputProps={{
                        autoComplete: 'new-password'
                    }}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end" className={CLASSES.inputAdornment}>
                                <div className={CLASSES.divider}></div>
                                <IconButton
                                    className={CLASSES.iconButton}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {shouldDisplayChecklist && (
                    <PasswordChecklist
                        rules={['match']}
                        value={password}
                        valueAgain={confirmPassword}
                        messages={{match: 'Passwords must match.'}}
                    />
                )}
            </fieldset>
            <div className={CLASSES.buttonsContainer}>
                <Button
                    onClick={requestPasswordChange}
                    className={CLASSES.sendChangePasswordRequest}
                >
                    Submit
                </Button>
                <Button
                    onClick={resendCode}
                    className={CLASSES.resendCodeButton}
                >
                    Resend Code
                </Button>
            </div>
        </div>
    );
}